import clsx from "clsx";

import { Fill, TableSkeleton } from "./skeletonPrimitives";

export function RaceNavigationSkeleton() {
  return (
    <div className="race-navigation-strip skeleton-race-navigation">
      {Array.from({ length: 10 }, (_, index) => (
        <div className="race-nav-card" key={index}>
          <Fill className="skeleton-line skeleton-line--short" />
          <Fill className="skeleton-line" />
          <Fill className="skeleton-line skeleton-line--medium" />
        </div>
      ))}
    </div>
  );
}

export function RacingDetailSkeleton() {
  return (
    <div className="skeleton-racing-detail">
      <div className="race-titlebar skeleton-race-title">
        <div>
          <Fill className="skeleton-line skeleton-line--short" />
          <Fill className="skeleton-race-title__heading" />
          <Fill className="skeleton-line skeleton-line--medium" />
        </div>
        <Fill className="skeleton-race-title__clock" />
      </div>
      <section className="panel race-viewer skeleton-viewer">
        <div className="race-viewer__toolbar">
          <Fill />
          <Fill />
        </div>
        <Fill className="skeleton-viewer__canvas" />
      </section>
      <div className="racing-layout">
        <section className="panel runner-market skeleton-market">
          <header className="runner-market__header">
            <Fill className="skeleton-line skeleton-line--short" />
            <div className="skeleton-tabs">
              <Fill />
              <Fill />
              <Fill />
            </div>
          </header>
          <TableSkeleton rows={8} />
        </section>
        <aside className="expert-tips">
          <section className="panel">
            <TipsSkeleton />
          </section>
          <Fill className="skeleton-fairness-link" />
        </aside>
      </div>
    </div>
  );
}

export function TipsSkeleton() {
  return (
    <div className="skeleton-tips">
      <Fill className="skeleton-line skeleton-line--medium" />
      <Fill className="skeleton-tips__selection" />
      <Fill className="skeleton-line" />
      <Fill className="skeleton-line skeleton-line--medium" />
      <Fill className="skeleton-line" />
      <Fill className="skeleton-line skeleton-line--medium" />
    </div>
  );
}

export function PackStageSkeleton() {
  return (
    <div className="skeleton-pack-stage">
      <header className="pack-opening-stage__header">
        <Fill className="skeleton-square" />
        <div>
          <Fill className="skeleton-line skeleton-line--medium" />
          <Fill className="skeleton-line" />
        </div>
        <Fill className="skeleton-square" />
      </header>
      <div className="pack-preview-cards">
        {Array.from({ length: 5 }, (_, index) => (
          <Fill className="pack-preview-card" key={index} />
        ))}
      </div>
      <div className="pack-purchase-controls">
        <Fill className="skeleton-control" />
        <Fill className="skeleton-control" />
        <Fill className="skeleton-cta" />
      </div>
      <div className="pack-probabilities">
        {Array.from({ length: 5 }, (_, index) => (
          <Fill key={index} />
        ))}
      </div>
    </div>
  );
}

export function ProfileSkeleton() {
  return (
    <>
      <section className="panel player-hero skeleton-profile">
        <Fill className="skeleton-profile__avatar" />
        <div>
          <Fill className="skeleton-profile__name" />
          <Fill className="skeleton-line skeleton-line--medium" />
          <Fill className="skeleton-line" />
        </div>
        <Fill className="skeleton-profile__links" />
      </section>
      <div className="player-stats skeleton-stats">
        {Array.from({ length: 6 }, (_, index) => (
          <section className="panel" key={index}>
            <Fill className="skeleton-square" />
            <div>
              <Fill className="skeleton-line skeleton-line--short" />
              <Fill className="skeleton-line" />
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

export function StableSkeleton({ rows }: { rows: number }) {
  return (
    <section className="panel stable-list skeleton-stable">
      <header>
        <div className="skeleton-tabs">
          <Fill />
          <Fill />
        </div>
        <Fill className="skeleton-line skeleton-line--short" />
      </header>
      <div className="stable-list__grid">
        {Array.from({ length: rows }, (_, index) => (
          <div className="stable-horse-row" key={index}>
            <Fill className="skeleton-stable__portrait" />
            <div>
              <Fill className="skeleton-line skeleton-line--medium" />
              <Fill className="skeleton-line skeleton-line--short" />
              <Fill className="skeleton-line" />
            </div>
            <div className="skeleton-stable__stats">
              <Fill />
              <Fill />
              <Fill />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MarketGridSkeleton() {
  return (
    <div className="market-grid skeleton-market-grid">
      {Array.from({ length: 4 }, (_, index) => (
        <section className="panel market-card" key={index}>
          <Fill className="skeleton-market-card__portrait" />
          <div className="market-card__body">
            <Fill className="skeleton-line skeleton-line--medium" />
            <Fill className="skeleton-line skeleton-line--short" />
            <Fill className="skeleton-line" />
            <Fill className="skeleton-market-card__price" />
          </div>
          <div className="market-card__actions">
            <Fill />
            <Fill />
          </div>
        </section>
      ))}
    </div>
  );
}

export function RoomSkeleton({
  rows,
  form = false,
}: {
  rows: number;
  form?: boolean;
}) {
  return (
    <div className={clsx("skeleton-rooms", form && "skeleton-rooms--form")}>
      {Array.from({ length: rows }, (_, index) => (
        <section className="panel syndicate-card" key={index}>
          <Fill className="skeleton-line skeleton-line--medium" />
          <Fill className="skeleton-line" />
          <Fill className="skeleton-room__field" />
          <Fill className="skeleton-room__field" />
          <Fill className="skeleton-cta" />
        </section>
      ))}
    </div>
  );
}

export function LeaderboardSkeleton({
  rows,
  hero = false,
}: {
  rows: number;
  hero?: boolean;
}) {
  return (
    <>
      {hero ? (
        <>
          <section className="panel leaderboard-hero skeleton-leaderboard-hero">
            <div>
              <Fill className="skeleton-line skeleton-line--short" />
              <Fill className="skeleton-leaderboard-hero__title" />
              <Fill className="skeleton-line" />
              <Fill className="skeleton-line skeleton-line--medium" />
            </div>
            <div className="skeleton-tabs">
              <Fill />
              <Fill />
              <Fill />
            </div>
          </section>
          <Fill className="skeleton-pool-banner" />
        </>
      ) : null}
      <section className="panel skeleton-leaderboard">
        <TableSkeleton rows={rows} />
      </section>
    </>
  );
}

export function VerifySkeleton({ rows }: { rows: number }) {
  return (
    <>
      <section className="panel verify-intro skeleton-verify-intro">
        <Fill className="skeleton-verify-intro__title" />
        <Fill className="skeleton-line" />
        <Fill className="skeleton-line skeleton-line--medium" />
        <div>
          <Fill className="skeleton-room__field" />
          <Fill className="skeleton-cta" />
        </div>
      </section>
      <section className="panel verification-results skeleton-verification">
        <TableSkeleton rows={rows} />
      </section>
    </>
  );
}
